import { roundCents, SUBSCRIPTION_UPKEEP_PER_TICK } from '../content/economy';
import { CREATION_KINDS } from '../content/creation-kinds';
import { tryDeliverTo } from './delivery';
import type { ObservableEntity } from './entities';
import type { SimEvent } from './events';
import { findSource } from './grid';
import { peekPort, tryPushToPort, tryTakeFromPort } from './port';
import { pickWeighted } from './random';
import { machinePorts } from './routing';
import type { SimState } from './state';
import { translate } from './types';

/** `of`'s cap is however many slots have been bought so far; `from`'s is its fixed `sequenceLength`; `interval` has none, which is the whole point of it. */
export function observableCap(observable: Pick<ObservableEntity, 'kind' | 'slotCosts'>): number {
  if (observable.kind === 'interval') return Infinity;
  return observable.kind === 'from'
    ? CREATION_KINDS.from.sequenceLength!
    : observable.slotCosts.length;
}

/** Null for an `interval` with no period picked yet, which is what keeps it a no-op until configured. */
export function observableRateTicks(
  observable: Pick<ObservableEntity, 'kind' | 'periodTicks'>,
): number | null {
  return CREATION_KINDS[observable.kind].rateTicks ?? observable.periodTicks;
}

/**
 * Edge-triggered, so a stream that sits finished for a thousand ticks still announces itself once —
 * and publishes to its channel, if it has been given one, which is the game's whole `destroy$`.
 */
function complete(state: SimState, observable: ObservableEntity, events: SimEvent[]): SimEvent[] {
  if (observable.completed) return events;
  observable.completed = true;
  const subject =
    observable.completesTo === null ? undefined : state.subjects[observable.completesTo];
  if (subject) subject.fired = true;
  events.push({ type: 'observableCompleted', observableId: observable.id });
  return events;
}

export function stepObservable(
  state: SimState,
  observable: ObservableEntity,
  random: () => number = Math.random,
): SimEvent[] {
  const events: SimEvent[] = [];

  const waiting = peekPort(observable.output);
  if (waiting !== undefined) {
    const { outSide } = machinePorts(state, observable.position);
    if (outSide) {
      const target = translate(observable.position, outSide);
      if (tryDeliverTo(state, target, waiting, events, observable.position))
        tryTakeFromPort(observable.output);
    }
  }

  if (!state.subscribed) return events;

  state.economy.cash = roundCents(state.economy.cash - SUBSCRIPTION_UPKEEP_PER_TICK);

  if (observable.cursor >= observableCap(observable)) return complete(state, observable, events);

  const source = findSource(state, observable.position);
  if (source && source.extracted >= source.capacity) return complete(state, observable, events);

  const rateTicks = observableRateTicks(observable);
  if (rateTicks === null) return events;

  observable.ticksSinceLastSpawn += 1;
  if (observable.ticksSinceLastSpawn < rateTicks) return events;
  if (observable.output.queue.length >= observable.output.capacity) return events;

  const material = pickWeighted(observable.yields, random);
  if (observable.kind !== 'interval') observable.sequence.push(material);
  if (source) source.extracted += 1;
  const packetId = state.nextPacketId++;
  state.packets[packetId] = { id: packetId, material, bornTick: state.tick };
  tryPushToPort(observable.output, packetId);
  observable.ticksSinceLastSpawn = 0;
  observable.cursor += 1;
  events.push({
    type: 'packetSpawned',
    packetId,
    material,
    position: observable.position,
  });

  return events;
}
